import { useState, useRef } from "react";
import { formatTL, resolveImage, effectivePrice } from "@/lib/api";
import { X, Check, Plus, ShoppingBag, CupSoda } from "lucide-react";
import { useT } from "@/lib/kioskI18n";

export const RemovablesModal = ({ product, onClose, onConfirm }) => {
  const { t, m } = useT();
  const [removed, setRemoved] = useState([]);
  const [added, setAdded] = useState([]);
  const [drink, setDrink] = useState(null);
  const [drinkWarn, setDrinkWarn] = useState(false);
  const drinkRef = useRef(null);
  const openedAt = useRef(Date.now());
  const closeFromBackdrop = () => {
    if (Date.now() - openedAt.current > 600) onClose();
  };

  const removables = product.removables || [];
  const extras = product.extras || [];
  const drinks = product.drinks || [];
  const drinkRequired = drinks.length > 0;

  const toggleRemoved = (item) => {
    setRemoved((prev) =>
      prev.includes(item) ? prev.filter((r) => r !== item) : [...prev, item]
    );
  };

  const toggleAdded = (extra) => {
    setAdded((prev) =>
      prev.some((e) => e.name === extra.name)
        ? prev.filter((e) => e.name !== extra.name)
        : [...prev, extra]
    );
  };

  const extraTotal = added.reduce((s, e) => s + (e.price || 0), 0);
  const drinkTotal = drink ? drink.price || 0 : 0;
  const finalPrice = effectivePrice(product) + extraTotal + drinkTotal;
  const canConfirm = !drinkRequired || !!drink;

  const handleConfirm = () => {
    if (canConfirm) return onConfirm({ removals: removed, added, drink });
    setDrinkWarn(true);
    drinkRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => setDrinkWarn(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-[75] grid place-items-center p-4">
      <div data-testid="removables-backdrop" onClick={closeFromBackdrop} className="absolute inset-0 bg-black/50" />
      <div
        data-testid="removables-modal"
        className="relative bg-white rounded-2xl w-full max-w-md sm:max-w-lg max-h-[90vh] max-h-[90dvh] flex flex-col overflow-hidden animate-fade-up"
      >
        <header className="shrink-0 bg-white flex items-center justify-between px-5 h-16 border-b border-[var(--fb-border)]">
          <div className="min-w-0">
            <h2 className="font-display font-bold text-lg truncate">{m(product.name)}</h2>
            <p className="text-xs text-[var(--fb-muted)]">{t("Siparişini özelleştir")}</p>
          </div>
          <button
            data-testid="removables-close-btn"
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto fb-scroll">
        {product.image_url && (
          <div className="h-36 overflow-hidden bg-[var(--fb-neutral)]">
            <img
              src={resolveImage(product.image_url)}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-5 space-y-5">
          {drinks.length > 0 && (
            <div ref={drinkRef} data-testid="drink-section" className={`space-y-3 rounded-2xl transition-colors ${drinkWarn ? "bg-red-50 ring-2 ring-[var(--fb-red)] p-2 animate-pulse" : ""}`}>
              <div
                data-testid="drink-required-banner"
                className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 border-2 ${drink ? "bg-green-50 border-green-600" : "bg-red-50 border-[var(--fb-red)]"}`}
              >
                <span className="flex items-center gap-2 font-display font-extrabold text-base">
                  <CupSoda className={`w-6 h-6 ${drink ? "text-green-700" : "text-[var(--fb-red)]"}`} />
                  {drink ? `${t("İçecek")}: ${m(drink.name)}` : t("Lütfen bir içecek seçin")}
                </span>
                <span className={`shrink-0 text-xs font-extrabold px-3 py-1 rounded-full text-white ${drink ? "bg-green-600" : "bg-[var(--fb-red)]"}`}>
                  {drink ? t("SEÇİLDİ") : t("ZORUNLU")}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
              {drinks.map((d) => {
                const isSelected = drink?.name === d.name;
                return (
                  <button
                    key={d.name}
                    type="button"
                    data-testid={`drink-select-${d.name}`}
                    onClick={() => setDrink(d)}
                    style={{ touchAction: "manipulation" }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-3 rounded-xl border-2 text-left text-sm font-bold transition-colors ${
                      isSelected
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-[var(--fb-border)] hover:border-blue-600"
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block leading-tight">{m(d.name)}</span>
                      {d.price > 0 && (
                        <span className="block text-xs font-bold text-blue-700 mt-0.5">+{formatTL(d.price)}</span>
                      )}
                    </span>
                    <span
                      className={`shrink-0 w-6 h-6 grid place-items-center rounded-full border-2 ${
                        isSelected ? "bg-blue-600 border-blue-600 text-white" : "border-[var(--fb-border)]"
                      }`}
                    >
                      {isSelected ? <Check className="w-3.5 h-3.5" /> : <span className="w-2 h-2 rounded-full bg-transparent" />}
                    </span>
                  </button>
                );
              })}
              </div>
            </div>
          )}

          {removables.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
                {t("Çıkarılabilir Malzemeler")}
              </p>
              {removables.map((item) => {
                const isRemoved = removed.includes(item);
                return (
                  <button
                    key={item}
                    type="button"
                    data-testid={`removable-toggle-${item}`}
                    onClick={() => toggleRemoved(item)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                      isRemoved
                        ? "border-[var(--fb-red)] bg-red-50 text-[var(--fb-red)] line-through"
                        : "border-[var(--fb-border)] hover:border-[var(--fb-red)]"
                    }`}
                  >
                    <span>{m(item)}</span>
                    <span
                      className={`w-6 h-6 grid place-items-center rounded-full border ${
                        isRemoved ? "bg-[var(--fb-red)] border-[var(--fb-red)] text-white" : "border-[var(--fb-border)]"
                      }`}
                    >
                      {isRemoved ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5 opacity-30" />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {extras.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider font-semibold text-[var(--fb-muted)]">
                {t("Eklenebilir Malzemeler")}
              </p>
              {extras.map((extra) => {
                const isAdded = added.some((e) => e.name === extra.name);
                return (
                  <button
                    key={extra.name}
                    type="button"
                    data-testid={`extra-toggle-${extra.name}`}
                    onClick={() => toggleAdded(extra)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                      isAdded
                        ? "border-green-600 bg-green-50 text-green-700"
                        : "border-[var(--fb-border)] hover:border-green-600"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {m(extra.name)}
                      <span className="text-xs font-bold text-green-700">
                        +{formatTL(extra.price)}
                      </span>
                    </span>
                    <span
                      className={`w-6 h-6 grid place-items-center rounded-full border ${
                        isAdded ? "bg-green-600 border-green-600 text-white" : "border-[var(--fb-border)]"
                      }`}
                    >
                      {isAdded ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5 opacity-30" />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        </div>

        <div className="shrink-0 bg-white border-t border-[var(--fb-border)] p-4">
          {drinkRequired && !drink && (
            <p data-testid="drink-required-hint" className="text-xs text-[var(--fb-red)] font-semibold text-center mb-2">
              {t("Lütfen bir içecek seçin")}
            </p>
          )}
          <button
            data-testid="removables-add-btn"
            onClick={handleConfirm}
            type="button"
            aria-disabled={!canConfirm}
            style={{ touchAction: "manipulation" }}
            className={`w-full flex items-center justify-center gap-2 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white font-bold text-lg py-4 rounded-full transition-colors active:scale-[0.99] ${canConfirm ? "" : "opacity-60"}`}
          >
            <ShoppingBag className="w-5 h-5" />
            {t("Sepete Ekle")}
            <span className="opacity-90">· {formatTL(finalPrice)}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
